import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { CLIENTALL, WORKALL } from "../../services/actions";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Checkbox,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton
} from "@mui/material";
import Swal from "sweetalert2";
import {
  BsFolderCheck,
  BsFileEarmarkZip,
  BsDownload,
  BsCheckAll,
  BsBuilding,
  BsCalendar3,
  BsFileEarmarkTextFill,
  BsCheckCircleFill,
  BsArrowClockwise
} from "react-icons/bs";
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt } from "react-icons/fa";

export default function FinalCopies() {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state?.loginState?.auth);
  const clientallState = useSelector((state) => state?.clientallState);
  const workallState = useSelector((state) => state?.workallState);

  const [clients, setClients] = useState([]);
  const [financialYears, setFinancialYears] = useState(["2026", "2025", "2024", "2023", "2022"]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedFY, setSelectedFY] = useState("");
  const [files, setFiles] = useState([]);
  const [works, setWorks] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const getAuthToken = () => {
    return auth?.token || auth?.access_token || auth?.access || localStorage.getItem("token") || "";
  };

  const getFileIcon = (filename = "") => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === "pdf") return <FaFilePdf color="#ef4444" size={20} />;
    if (["doc", "docx"].includes(ext)) return <FaFileWord color="#3b82f6" size={20} />;
    if (["xls", "xlsx", "csv"].includes(ext)) return <FaFileExcel color="#10b981" size={20} />;
    return <FaFileAlt color="#8b5cf6" size={20} />;
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchClientData(selectedClient, selectedFY);
  }, []);

  const fetchClientData = async (clientId = "", fy = "") => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const apiBase = (process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/').replace(/\/+$/, '');
      let url = `${apiBase}/final-copies/`;
      const params = [];
      if (clientId) params.push(`client_id=${clientId}`);
      if (fy) params.push(`financial_year=${fy}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const res = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.data) {
        if (res.data.clients && res.data.clients.length > 0) {
          setClients(res.data.clients);
          if (!selectedClient && !clientId && res.data.clients.length > 0) {
            setSelectedClient(res.data.clients[0].id);
            fetchClientData(res.data.clients[0].id, fy);
            return;
          }
        }
        if (res.data.financial_years && res.data.financial_years.length > 0) {
          setFinancialYears((prev) => Array.from(new Set([...prev, ...res.data.financial_years])));
        }
        if (res.data.works) {
          setWorks(res.data.works);
        }
        const fileList = res.data.files || [];
        setFiles(fileList);
        setSelectedFileIds(fileList.map((f, idx) => f.id || idx));
      }
    } catch (err) {
      console.error("Failed to fetch final copy data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData(selectedClient, selectedFY);
  }, [auth]);

  const handleClientChange = (e) => {
    const cid = e.target.value;
    setSelectedClient(cid);
    fetchClientData(cid, selectedFY);
  };

  const handleFYChange = (e) => {
    const fy = e.target.value;
    setSelectedFY(fy);
    fetchClientData(selectedClient, fy);
  };

  const handleToggleSelectAll = () => {
    if (selectedFileIds.length === files.length) {
      setSelectedFileIds([]);
    } else {
      setSelectedFileIds(files.map((f, idx) => f.id || idx));
    }
  };

  const handleToggleFile = (fileId) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const handleGenerateFinalCopies = async () => {
    if (!selectedClient) {
      Swal.fire({ icon: "warning", title: "Select Client", text: "Please select a client first." });
      return;
    }
    if (selectedFileIds.length === 0) {
      Swal.fire({ icon: "warning", title: "No Files Selected", text: "Please select at least one file to create final copies." });
      return;
    }

    const selectedFileObjects = files.filter((f, idx) => selectedFileIds.includes(f.id || idx));

    const confirm = await Swal.fire({
      title: "Generate Final Copies?",
      text: `Are you sure you want to merge and save ${selectedFileObjects.length} files into the 'Final Copies' folder for this company?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Yes, Generate Now"
    });

    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      const token = getAuthToken();
      const apiBase = (process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/').replace(/\/+$/, '');
      const url = `${apiBase}/final-copies/`;
      const payload = {
        client_id: selectedClient,
        financial_year: selectedFY || "all",
        selected_files: selectedFileObjects
      };

      const res = await axios.post(url, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.data) {
        setLastResult(res.data);
        const serverHost = (process.env.REACT_APP_URL || 'http://127.0.0.1:8000/').replace(/\/api\/?$/, '');
        const fullZipUrl = (res.data.zip_url && res.data.zip_url.startsWith('http'))
          ? res.data.zip_url
          : `${serverHost.replace(/\/+$/, '')}${res.data.zip_url}`;

        Swal.fire({
          icon: "success",
          title: "Final Copies Created!",
          html: `<b>${res.data.total_files} files</b> have been consolidated into the <code>final_copies</code> folder.<br/><br/>
                 <a href="${fullZipUrl}" download target="_blank" rel="noreferrer" class="swal2-confirm swal2-styled" style="display:inline-block; margin-top:8px; text-decoration:none; background:#10b981; padding:10px 20px; border-radius:8px; font-weight:700; color:#ffffff;">
                   Download Final ZIP Archive
                 </a>`,
          showConfirmButton: true,
          confirmButtonText: "Done",
          confirmButtonColor: "#6366f1"
        });
      }
    } catch (err) {
      console.error("Failed to generate final copies:", err);
      Swal.fire({
        icon: "error",
        title: "Generation Failed",
        text: err?.response?.data?.error || err?.message || "Could not generate final copies."
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentClientObj = clients.find((c) => String(c.id) === String(selectedClient)) || {};

  const hasAccess = auth?.is_superuser || 
    (auth?.permissions || []).some(p => p.includes("can_view_final_doc") || p.includes("view_final_copies") || p === "*") ||
    (auth?.groups || []).some(g => ["Work Admin", "Work Manager", "Super Admin", "SuperAdmin", "Admin"].includes(g));

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Card sx={{ p: 4, maxWidth: 480, textAlign: "center", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <BsFolderCheck style={{ fontSize: 52, color: "#94a3b8", marginBottom: 16 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 1 }}>
            Access Restricted
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
            You do not have permission to access Final Copies repository management. Please contact your Work Admin or Manager if you require access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.href = "#/"}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, px: 3 }}
          >
            Back to Dashboard
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", background: "#f8fafc", width: "100%", boxSizing: "border-box" }}>
      {/* Header Banner */}
      <Box sx={{ mb: 3.5, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: 1.5 }}>
            <BsFolderCheck color="#6366f1" size={28} />
            Final Copies Manager
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, fontWeight: 500 }}>
            Select client works, inspect latest file versions, and consolidate approved files into the official <b>Final Copies</b> repository.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <Button
            variant="outlined"
            onClick={() => fetchClientData(selectedClient, selectedFY)}
            disabled={loading}
            startIcon={<BsArrowClockwise />}
            sx={{
              borderRadius: "10px",
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              textTransform: "none",
              px: 2,
              "&:hover": { borderColor: "#6366f1", background: "rgba(99,102,241,0.04)" }
            }}
          >
            Refresh Files
          </Button>

          <Button
            variant="contained"
            onClick={handleGenerateFinalCopies}
            disabled={submitting || files.length === 0 || selectedFileIds.length === 0}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <BsFolderCheck />}
            sx={{
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              color: "#ffffff",
              fontWeight: 700,
              textTransform: "none",
              px: 2.5,
              py: 1,
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
              "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)" }
            }}
          >
            {submitting ? "Merging Copies..." : `Merge & Save Final Copies (${selectedFileIds.length})`}
          </Button>
        </Box>
      </Box>

      {/* Filter Selection Card */}
      <Card sx={{ p: 3, mb: 3.5, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6} lg={5}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <BsBuilding color="#6366f1" /> Select Company / Client
            </Typography>
            <select
              value={selectedClient}
              onChange={handleClientChange}
              style={{
                width: "100%",
                padding: "11px 16px",
                borderRadius: "10px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                fontSize: "14px",
                fontWeight: 600,
                color: "#0f172a",
                outline: "none",
                cursor: "pointer"
              }}
            >
              <option value="">-- Select Client --</option>
              {clients.map((c, idx) => {
                const label = c.business_name || c.company_name || c.client_name || c.name || `Client #${c.id || idx+1}`;
                const panInfo = c.pan ? ` (${c.pan})` : (c.gstin ? ` (${c.gstin})` : "");
                return (
                  <option key={c.id || idx} value={c.id}>
                    {label}{panInfo}
                  </option>
                );
              })}
            </select>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#475569", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <BsCalendar3 color="#10b981" /> Financial Year
            </Typography>
            <select
              value={selectedFY}
              onChange={handleFYChange}
              style={{
                width: "100%",
                padding: "11px 16px",
                borderRadius: "10px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                fontSize: "14px",
                fontWeight: 600,
                color: "#0f172a",
                outline: "none",
                cursor: "pointer"
              }}
            >
              <option value="">All Financial Years</option>
              {financialYears.map((fy, idx) => (
                <option key={idx} value={fy}>
                  FY {fy}
                </option>
              ))}
            </select>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: { xs: "flex-start", lg: "flex-end" }, mt: { xs: 1, lg: 3 } }}>
              <Box sx={{ textAlign: "center", px: 2, py: 1, background: "#f1f5f9", borderRadius: 2, minWidth: 90 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Works</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{works.length}</Typography>
              </Box>
              <Box sx={{ textAlign: "center", px: 2, py: 1, background: "#e0e7ff", borderRadius: 2, minWidth: 90 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase" }}>Latest Files</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#4338ca" }}>{files.length}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* Generated Result Banner */}
      {lastResult && (
        <Card sx={{ p: 2.5, mb: 3.5, borderRadius: 3, background: "#ecfdf5", border: "1px solid #a7f3d0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <BsCheckCircleFill color="#10b981" size={24} />
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#065f46", fontSize: 14 }}>
                {lastResult.message}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#047857" }}>
                Target Folder: <code>{lastResult.folder_path}</code>
              </Typography>
            </Box>
          </Box>
          <Button
            href={lastResult.zip_url}
            download
            variant="contained"
            startIcon={<BsDownload />}
            sx={{
              background: "#10b981",
              color: "#ffffff",
              fontWeight: 700,
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": { background: "#059669" }
            }}
          >
            Download Final ZIP
          </Button>
        </Card>
      )}

      {/* Files Table Card */}
      <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.02)", overflow: "hidden" }}>
        {/* Table Header Controls */}
        <Box sx={{ p: 2.5, background: "#ffffff", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={files.length > 0 && selectedFileIds.length === files.length}
              indeterminate={selectedFileIds.length > 0 && selectedFileIds.length < files.length}
              onChange={handleToggleSelectAll}
              disabled={files.length === 0}
              sx={{ color: "#6366f1", "&.Mui-checked": { color: "#6366f1" } }}
            />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
              Latest Unique Documents ({selectedFileIds.length} of {files.length} selected)
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
            * Showing only the latest versions from each task/work
          </Typography>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#6366f1" }} />
          </Box>
        )}

        {/* Table Content */}
        {!loading && files.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 16px", width: 40 }}></th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#475569" }}>Document Name</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#475569" }}>Associated Work</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#475569" }}>Financial Year</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#475569" }}>Category</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#475569" }}>Last Updated</th>
                  <th style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#475569", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file, index) => {
                  const fid = file.id || index;
                  const isSelected = selectedFileIds.includes(fid);
                  const fname = file.original_filename || file.uuid_filename || `Document ${index + 1}`;
                  const createdDate = file.created_at ? new Date(file.created_at).toLocaleDateString() : "Latest";

                  return (
                    <tr
                      key={index}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isSelected ? "rgba(99, 102, 241, 0.02)" : "#ffffff",
                        transition: "background 0.2s ease"
                      }}
                    >
                      <td style={{ padding: "12px 16px" }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleFile(fid)}
                          sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#6366f1" } }}
                        />
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {getFileIcon(fname)}
                          <Box>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                              {fname}
                            </Typography>
                            {file.commit_message && (
                              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                {file.commit_message}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </td>

                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#334155", fontWeight: 600 }}>
                        {file.work_name || "General"}
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <Chip
                          label={file.financial_year || selectedFY || "N/A"}
                          size="small"
                          sx={{ background: "#e0e7ff", color: "#4338ca", fontWeight: 700, fontSize: 11 }}
                        />
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <Chip
                          label={file.category || "Work File"}
                          size="small"
                          sx={{ background: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: 11 }}
                        />
                      </td>

                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#64748b" }}>
                        {createdDate}
                      </td>

                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        {file.id && (
                          <Tooltip title="Direct Download">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const repoBase = (process.env.REACT_APP_REPO_URL || 'https://repo.plakal.in').replace(/\/+$/, '');
                                const dl = `${repoBase}/api/download/${file.id}/`;
                                window.open(dl, "_blank");
                              }}
                              sx={{ color: "#6366f1" }}
                            >
                              <BsDownload size={15} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}

        {/* Empty State */}
        {!loading && files.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
            <BsFolderCheck style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 12 }} />
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#475569" }}>
              No documents found
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.5 }}>
              {selectedClient
                ? "No files have been committed or uploaded for this company yet."
                : "Please select a client from the dropdown above to view documents."}
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
}
